/**
 * F20 (Wave 2D) — admin dispute-history panel.
 *
 * Renders historical (`resolved_*`, `withdrawn`) dispute rows for a single
 * booking. Mirrors the F17 history-pane visual treatment but uses small inline
 * Cards rather than `AuditLogTimeline` because each row carries an admin-only
 * resolution-notes field that deserves its own block.
 *
 * Rare in practice — most bookings have at most one open dispute and zero
 * historic ones — but supported so admin can trace a repeat-disputer's
 * timeline in one panel. Returns `null` when the array is empty.
 */
import { useI18n } from '@bravemove/i18n/react';
import { Badge, Card, Chip } from '@bravemove/ui-web';
import type { PaymentDispute, PaymentDisputeStatus } from '@bravemove/types';

export interface DisputeHistoryPanelProps {
  readonly disputes: readonly PaymentDispute[];
  readonly title?: string;
}

function statusBadgeTone(status: PaymentDisputeStatus): 'success' | 'danger' | 'neutral' | 'info' {
  switch (status) {
    case 'resolved_customer_correct':
      return 'success';
    case 'resolved_venue_correct':
      return 'info';
    case 'withdrawn':
      return 'neutral';
    case 'open':
    default:
      return 'danger';
  }
}

function statusLabelKey(status: PaymentDisputeStatus): string {
  return `payment:outcome.${status}`;
}

export function DisputeHistoryPanel({ disputes, title }: DisputeHistoryPanelProps) {
  const { t } = useI18n();
  const resolvedTitle =
    title ?? t('payment:dispute_history.title', { defaultValue: 'Previous disputes' });

  if (disputes.length === 0) {
    return null;
  }

  return (
    <Card>
      <div className="flex flex-col gap-3" data-testid="dispute-history-panel">
        <h2 className="text-h2 font-bold text-ink">{resolvedTitle}</h2>
        <ol className="flex flex-col gap-2">
          {disputes.map((d) => (
            <li
              key={d.id}
              className="rounded-md border border-hairline bg-surface-2 p-3 flex flex-col gap-1.5"
              data-testid={`dispute-history-row-${d.id}`}
            >
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2">
                  <Badge tone={statusBadgeTone(d.status)} size="sm">
                    {t(statusLabelKey(d.status), {
                      defaultValue:
                        d.status === 'resolved_customer_correct'
                          ? 'Customer correct'
                          : d.status === 'resolved_venue_correct'
                            ? 'Venue correct'
                            : d.status === 'withdrawn'
                              ? 'Withdrawn'
                              : 'Open',
                    })}
                  </Badge>
                  <Chip>
                    {t('payment:dispute_history.filed_label', {
                      defaultValue: 'Filed',
                    })}{' '}
                    {d.createdAt}
                  </Chip>
                </div>
                {d.resolvedAt ? (
                  <span className="text-[11px] font-mono tabular-nums text-ink-3">
                    {t('payment:dispute_history.resolved_label', {
                      defaultValue: 'Resolved',
                    })}{' '}
                    {d.resolvedAt}
                  </span>
                ) : null}
              </div>
              <p className="text-[12.5px] text-ink leading-snug">
                <span className="text-ink-3 font-mono uppercase tracking-wider text-[10.5px] me-1.5">
                  {t('payment:dispute_history.reason_label', { defaultValue: 'Reason' })}
                </span>
                {d.reason}
              </p>
              {d.resolutionNotes ? (
                <p className="text-[12.5px] text-ink-2 leading-snug">
                  <span className="text-ink-3 font-mono uppercase tracking-wider text-[10.5px] me-1.5">
                    {t('payment:dispute_history.resolution_label', {
                      defaultValue: 'Resolution notes',
                    })}
                  </span>
                  {d.resolutionNotes}
                </p>
              ) : null}
            </li>
          ))}
        </ol>
      </div>
    </Card>
  );
}
